import { AppError } from './AppError';

/**
 * Error thrown when a request is invalid.
 */
export class BadRequestError extends AppError {
  /**
   * Creates a new BadRequestError instance.
   * @param message The error message.
   * @param invalidParams The invalid parameters.
   */
  constructor(
    message: string = 'Bad request',
    invalidParams?: Record<string, string>
  ) {
    const formattedMessage = invalidParams
      ? `${message}: ${Object.entries(invalidParams).map(([key, value]) => `${key}: ${value}`).join(', ')}`
      : message;
    
    super(formattedMessage, 'BAD_REQUEST', true, { invalidParams });
    this.name = 'BadRequestError';
  }

  /**
   * Creates a BadRequestError for specific invalid parameters.
   * @param invalidParams The invalid parameters.
   * @returns A new BadRequestError instance.
   */
  static forParams(invalidParams: Record<string, string>): BadRequestError {
    return new BadRequestError(undefined, invalidParams);
  }

  /**
   * Creates a BadRequestError for a missing parameter.
   * @param paramName The name of the missing parameter.
   * @returns A new BadRequestError instance.
   */
  static missingParam(paramName: string): BadRequestError {
    return new BadRequestError(`Missing required parameter: ${paramName}`);
  }

  /**
   * Creates a BadRequestError for an invalid parameter value.
   * @param paramName The name of the parameter.
   * @param value The invalid value.
   * @param reason The reason why the value is invalid.
   * @returns A new BadRequestError instance.
   */
  static invalidParamValue(paramName: string, value: any, reason?: string): BadRequestError {
    const message = reason
      ? `Invalid value for parameter ${paramName}: ${value} (${reason})`
      : `Invalid value for parameter ${paramName}: ${value}`;
    return new BadRequestError(message);
  }
}